import { useState, useEffect } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Skeleton from '@mui/material/Skeleton';
import Avatar from '@mui/material/Avatar';
import Chip from '@mui/material/Chip';
import RequestAcceptForm from '@UserManagementForms/RequestAcceptForm';
import UserAuthService from '@Helpers/URLHelpers/UserAuthService';
import RequestService from '@Helpers/URLHelpers/RequestService';
import InvitationService from '@Helpers/URLHelpers/InvitationService';
import { useReduxActiveUser } from '@Hooks/reduxActiveUserHook/useReduxActiveUser';
import ErrorHandler from '@Root/ErrorHandler';
import {
    IoReloadOutline,
    IoMailOpen,
    IoPersonAdd,
    IoBusiness,
    IoPeople,
    IoCheckmarkCircle,
    IoCloseCircle,
    IoTime,
    IoShieldCheckmark
} from 'react-icons/io5';
import '@Partials/_ComStyles.scss';
import '@Partials/_SimpleStyles.scss';
import '@Partials/_OverrideStyles.scss';
import './AccessSettingsPage.scss';

export default function AccessSettingsPage(props) {
    const [acceptRequest, setAcceptRequest] = useState({});
    const [cursor, setCursor] = useState('auto');
    const [requests, setRequests] = useState([]);
    const [invitations, setInvitations] = useState([]);
    const [showRequestAcceptForm, setShowRequestAcceptForm] = useState(false);
    const [showRequestsDot, setShowRequestsDot] = useState(false);
    const [showInvitationsDot, setShowInvitationsDot] = useState(false);
    const [accessCenterTabView, setAccessCenterTabView] = useState(0);
    const [loading, setLoading] = useState(true);

    const { activeUserId, setActiveUserPermission, setActiveUserAccessList } = useReduxActiveUser();
    const userAuthService = new UserAuthService();
    const requestService = new RequestService();
    const invitationService = new InvitationService();
    const errorHandler = new ErrorHandler();
    const className = 'accessSettingsPage';

    const colorList = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
        '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
        '#9158a9', '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
        '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];

    useEffect(() => {
        renderData();
    }, []);

    const renderData = async () => {
        try {
            setLoading(true);
            let requestsData = await requestService.getRequestListForUser(activeUserId);
            requestsData = JSON.parse(requestsData.body) || [];
            let invitationsData = await invitationService.getInvitationListForUser(activeUserId);
            invitationsData = JSON.parse(invitationsData.body) || [];
            setRequests(requestsData);
            setInvitations(invitationsData);
            setShowRequestsDot((requestsData?.length || 0) > 0);
            setShowInvitationsDot((invitationsData?.length || 0) > 0);
        } catch (err) {
            errorHandler.handleError(err);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        renderData();
    };

    const handleAcceptRequest = (request) => {
        setAcceptRequest(request);
        toggleInviteAcceptForm();
    };

    const handleDenyRequest = async (request) => {
        setCursor('wait');
        try {
            await requestService.denyRequest(request);
            renderData();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const toggleInviteAcceptForm = () => {
        setShowRequestAcceptForm(!showRequestAcceptForm);
    };

    const handleAcceptInvitation = async (invitation) => {
        setCursor('wait');
        try {
            await invitationService.acceptInvitation(invitation);
            let updatedPermissions = await userAuthService.getUserPermissions(activeUserId);
            updatedPermissions = JSON.parse(updatedPermissions.body);
            setActiveUserPermission(updatedPermissions);
            let userAccessList = await userAuthService.getAccessListForUser(activeUserId);
            userAccessList = JSON.parse(userAccessList.body);
            setActiveUserAccessList(userAccessList);
            renderData();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const handleDenyInvitation = async (invitation) => {
        setCursor('wait');
        try {
            await invitationService.denyInvitation(invitation);
            renderData();
        } catch (err) {
            errorHandler.handleError(err);
        }
        setCursor('auto');
    };

    const toggleAccessCenterTabView = (e, view) => {
        setAccessCenterTabView(view);
    };

    const getInitials = (string) => {
        if (!!string && string.length > 0) {
            var names = string?.split(' '),
                initials = names[0]?.substring(0, 1)?.toUpperCase();
            if (names?.length > 1) {
                initials += names[names?.length - 1]?.substring(0, 1)?.toUpperCase();
            }
            return initials;
        }
        return 'N/A';
    };

    const EmptyState = ({ title, subtitle, icon: Icon }) => (
        <div className={`${className}__empty`}>
            <div className={`${className}__empty__icon`}>
                <Icon />
            </div>
            <h3 className={`${className}__empty__title`}>{title}</h3>
            {subtitle && <p className={`${className}__empty__subtitle`}>{subtitle}</p>}
            <Button
                startIcon={<IoReloadOutline />}
                variant="outlined"
                size="medium"
                onClick={handleRefresh}
                className={`${className}__empty__refresh`}
            >
                Refresh
            </Button>
        </div>
    );

    const LoadingList = () => (
        <div className={`${className}__list`}>
            {[...Array(3)].map((_, i) => (
                <div key={i} className={`${className}__card`}>
                    <div className={`${className}__card__avatar`}>
                        <Skeleton variant="circular" width={56} height={56} />
                    </div>
                    <div className={`${className}__card__content`}>
                        <Skeleton width="70%" height={22} />
                        <Skeleton width="50%" height={18} sx={{ mt: 0.5 }} />
                        <Skeleton width="40%" height={16} sx={{ mt: 0.5 }} />
                    </div>
                    <div className={`${className}__card__actions`}>
                        <Skeleton variant="rounded" width={100} height={36} />
                        <Skeleton variant="rounded" width={80} height={36} />
                    </div>
                </div>
            ))}
        </div>
    );

    const InvitationsList = () => {
        if (loading) return <LoadingList />;
        if (!invitations || invitations.length === 0) {
            return (
                <EmptyState
                    icon={IoMailOpen}
                    title="No Invitations"
                    subtitle="You don't have any pending invitations at the moment."
                />
            );
        }
        return (
            <div className={`${className}__list`}>
                {invitations.map((inv, idx) => {
                    const isOrgInvite = !inv.teamId || inv.teamId === 0 || inv.teamId === '0';
                    return (
                        <div key={inv?.id || idx} className={`${className}__card`}>
                            <div className={`${className}__card__avatar`}>
                                <Avatar
                                    sx={{
                                        bgcolor: colorList[inv.inviterFullName?.charCodeAt(0) % colorList.length],
                                        width: 56,
                                        height: 56,
                                        fontSize: '1.25rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {getInitials(inv.inviterFullName)}
                                </Avatar>
                            </div>
                            <div className={`${className}__card__content`}>
                                <div className={`${className}__card__header`}>
                                    <h4 className={`${className}__card__title`}>
                                        {inv.inviterFullName}
                                    </h4>
                                    <Chip
                                        icon={isOrgInvite ? <IoBusiness /> : <IoPeople />}
                                        label={isOrgInvite ? 'Organization' : 'Team'}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                </div>
                                <p className={`${className}__card__subtitle`}>
                                    @{inv.inviterUserName}
                                </p>
                                <p className={`${className}__card__description`}>
                                    Invited you to join <strong>{isOrgInvite ? inv.orgName : inv.teamName}</strong>
                                </p>
                            </div>
                            <div className={`${className}__card__actions`}>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={<IoCheckmarkCircle />}
                                    onClick={() => handleAcceptInvitation(inv)}
                                    disabled={cursor === 'wait'}
                                    className={`${className}__card__accept`}
                                >
                                    Accept
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="error"
                                    startIcon={<IoCloseCircle />}
                                    onClick={() => handleDenyInvitation(inv)}
                                    disabled={cursor === 'wait'}
                                    className={`${className}__card__deny`}
                                >
                                    Deny
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    const RequestsList = () => {
        if (loading) return <LoadingList />;
        if (!requests || requests.length === 0) {
            return (
                <EmptyState
                    icon={IoTime}
                    title="No Pending Requests"
                    subtitle="There are no requests awaiting your approval."
                />
            );
        }
        return (
            <div className={`${className}__list`}>
                {requests.map((req, idx) => {
                    const isOrgRequest = req.requestType === 'org';
                    return (
                        <div key={req?.id || idx} className={`${className}__card`}>
                            <div className={`${className}__card__avatar`}>
                                <Avatar
                                    sx={{
                                        bgcolor: colorList[req.requesterFullName?.charCodeAt(0) % colorList.length],
                                        width: 56,
                                        height: 56,
                                        fontSize: '1.25rem',
                                        fontWeight: 700
                                    }}
                                >
                                    {getInitials(req.requesterFullName)}
                                </Avatar>
                            </div>
                            <div className={`${className}__card__content`}>
                                <div className={`${className}__card__header`}>
                                    <h4 className={`${className}__card__title`}>
                                        {req.requesterFullName}
                                    </h4>
                                    <Chip
                                        icon={isOrgRequest ? <IoBusiness /> : <IoPeople />}
                                        label={isOrgRequest ? 'Organization' : 'Team'}
                                        size="small"
                                        variant="outlined"
                                        color="primary"
                                    />
                                </div>
                                <p className={`${className}__card__subtitle`}>
                                    @{req.requesterUserName}
                                </p>
                                <p className={`${className}__card__description`}>
                                    Requests to join <strong>{isOrgRequest ? req.orgName : req.teamName}</strong>
                                </p>
                            </div>
                            <div className={`${className}__card__actions`}>
                                <Button
                                    variant="contained"
                                    size="medium"
                                    startIcon={<IoShieldCheckmark />}
                                    onClick={() => handleAcceptRequest(req)}
                                    disabled={cursor === 'wait'}
                                    className={`${className}__card__accept`}
                                >
                                    Review
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="error"
                                    startIcon={<IoCloseCircle />}
                                    onClick={() => handleDenyRequest(req)}
                                    disabled={cursor === 'wait'}
                                    className={`${className}__card__deny`}
                                >
                                    Deny
                                </Button>
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    return (
        <div className={className} style={{ cursor }}>
            <div className={`${className}__header`}>
                <div className={`${className}__header__icon`}>
                    <IoPersonAdd />
                </div>
                <div className={`${className}__header__content`}>
                    <h1 className={`${className}__header__title`}>Access Center</h1>
                    <p className={`${className}__header__subtitle`}>
                        Manage your invitations and review incoming access requests
                    </p>
                </div>
                <IconButton
                    onClick={handleRefresh}
                    className={`${className}__header__refresh`}
                    title="Refresh"
                >
                    <IoReloadOutline />
                </IconButton>
            </div>

            <div className={`${className}__tabs`}>
                <Tabs
                    value={accessCenterTabView}
                    onChange={toggleAccessCenterTabView}
                    className={`${className}__tabs__bar`}
                    variant="fullWidth"
                >
                    <Tab
                        className={`${className}__tab`}
                        icon={<IoMailOpen />}
                        iconPosition="start"
                        label={
                            <Badge
                                badgeContent={invitations.length}
                                color="error"
                                max={99}
                                invisible={!showInvitationsDot}
                            >
                                <span style={{ marginRight: showInvitationsDot ? 16 : 0 }}>Invitations</span>
                            </Badge>
                        }
                    />
                    <Tab
                        className={`${className}__tab`}
                        icon={<IoTime />}
                        iconPosition="start"
                        label={
                            <Badge
                                badgeContent={requests.length}
                                color="error"
                                max={99}
                                invisible={!showRequestsDot}
                            >
                                <span style={{ marginRight: showRequestsDot ? 16 : 0 }}>Pending Requests</span>
                            </Badge>
                        }
                    />
                </Tabs>
            </div>

            <div className={`${className}__content`}>
                {accessCenterTabView === 0 && <InvitationsList />}
                {accessCenterTabView === 1 && <RequestsList />}
            </div>

            {showRequestAcceptForm && (
                <RequestAcceptForm
                    open={showRequestAcceptForm}
                    closePopup={toggleInviteAcceptForm}
                    request={acceptRequest}
                    errorHandler={props.errorHandler}
                    reloadData={renderData}
                />
            )}
        </div>
    );
}

